import { useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectEmail, selectSession, selectError, selectPassword, selectSuccessMessage } from '../../redux-state/userAuth/selectors';
import maskEmail from '../../components/common/MaskEmail';
import { isLoggedIn } from '../../components/authProvider/AuthContext';
import PrimaryButton from '../../components/common/button/PrimaryButton';

function EmailVerification() {
  const [otp, setOtp] = useState(new Array(6).fill(''));
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const inputRefs = useRef([]);
  const email = useSelector(selectEmail);
  const session = useSelector(selectSession);
  const error = useSelector(selectError);
  const successMessage = useSelector(selectSuccessMessage);

  useEffect(() => {
    if (isLoggedIn()) {
      navigate('/dashboard');
    }
  }, [navigate]);

  useEffect(() => {
    dispatch({ type: 'CLEAR_MFA_ERROR' });
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      setOtp(new Array(6).fill(''));
      inputRefs.current[0]?.focus();
      setLoading(false);
    }
  }, [error]);

  const handleChange = (e, index) => {
    const inputValue = e.target.value.replace(/[^0-9]/g, '');
    if (!inputValue) return;

    const newOtp = [...otp];
    newOtp[index] = inputValue[0];
    setOtp(newOtp);

    if (error) {
      dispatch({ type: 'CLEAR_MFA_ERROR' });
    }

    if (index < 5 && inputValue) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, 6);

    if (paste.length === 0) return;

    const newOtp = [...otp];
    for (let i = 0; i < 6; i++) {
      newOtp[i] = paste[i] || '';
    }

    setOtp(newOtp);
    inputRefs.current[Math.min(paste.length - 1, 5)]?.focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace') {
      const newOtp = [...otp];

      if (otp[index]) {
        newOtp[index] = '';
        setOtp(newOtp);
      } else if (index > 0) {
        newOtp[index - 1] = '';
        setOtp(newOtp);
        inputRefs.current[index - 1]?.focus();
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    mfa_authentication();
  };

  const combinedOtp = otp.join('');

  const mfa_authentication = () => {
    dispatch({
      type: 'MFA_AUTHENTICATION',
      payload: { otp_code: combinedOtp, mfa_username: email, mfa_session: session },
      navigate: navigate,
    });
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  const handleResendCode = () => {
    dispatch({
      type: 'RESEND_CODE_MFA',
    });
  };

  useEffect(() => {
    return () => {
      dispatch({ type: 'CLEAR_SUCCESS_MESSAGE' });
    };
  }, [dispatch]);

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <form className="bg-white rounded-2xl shadow-md p-10 min-w-[30%] w-[500px]" onSubmit={handleSubmit}>
        <div className="text-center mb-10">
          <div className="flex items-center justify-center mb-4">
            <img className="h-16 w-16" src="/logo.png" alt="logo" style={{ width: "100px", height: "100px" }}/>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Email Verification
          </h2>

          <p className="text-gray-600 text-sm">
            Please enter the verification code you received in your email{' '}
            <span className="font-medium">{maskEmail(email)}</span>
          </p>
        </div>

        <div className="mb-6">
          <div className="flex justify-center gap-2 mb-4">
            {otp.map((value, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                maxLength="1"
                value={value}
                onChange={(e) => handleChange(e, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onPaste={handlePaste}
                className="w-12 h-12 text-center text-xl font-semibold border-2 border-gray-300 rounded-md focus:outline-none focus:border-blue-500 transition-colors"
              />
            ))}
          </div>

          {error && (
            <div className="text-center text-sm text-red-500 mb-2">
              {error}
            </div>
          )}
          {successMessage && (
            <div className="text-center text-sm text-green-600 mb-2">
              {successMessage}
            </div>
          )}

          <div className="text-center">
            <span className="text-gray-600 text-sm">Didn't receive the verification code? </span>
            <button
              type="button"
              className="text-green-800 text-sm font-medium hover:text-green-900 hover:underline"
              onClick={handleResendCode}
            >
              Resend Code?
            </button>
          </div>
        </div>

        <PrimaryButton
          text={loading ? 'Verifying...' : 'Verify'}
          type="submit"
          disabled={loading}
          className="w-full"
        />

        <div className="text-center mt-8">
          <button
            type="button"
            className="text-gray-600 text-sm hover:text-gray-800 hover:underline transition-colors"
            onClick={handleBackToLogin}
          >
            Back to Login
          </button>
        </div>
      </form>
    </div>
  );
}

export default EmailVerification;
